import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, RefreshCcw, Loader2, ExternalLink } from "lucide-react";

/**
 * Magic demo-link button — generates a single URL stakeholders can click to be
 * auto-signed-in as a demo learner and land on this course's full learner UX
 * (Aria companion, quizzes, progress). No OTP, no login friction.
 */
export default function DemoLinkButton({ courseId }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const url = token ? `${window.location.origin}/demo/${token}` : "";

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/share/courses/${courseId}/demo-link/regenerate`);
      setToken(data.demo_token);
      toast.success("Demo link ready", { description: "Send it to anyone — they'll get the full learner experience." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate demo link");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Demo link copied", { description: url });
    } catch { toast.error("Copy failed"); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && !token) generate(); }}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          className="h-9 gap-1.5 bg-[#222222] hover:bg-[#0a0a0a] text-white"
          data-testid="demo-link-button"
        >
          <Sparkles size={13}/> Demo link
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-4" data-testid="demo-link-popover">
        <div className="text-[10px] uppercase tracking-wider text-[#E53935] font-bold mb-1 inline-flex items-center gap-1.5">
          <Sparkles size={11}/> One-click stakeholder demo
        </div>
        <h3 className="text-sm font-display font-bold text-slate-900 mb-3">
          Anyone with this link sees the full learner experience — no OTP, no sign-up.
        </h3>
        <div className="flex items-stretch gap-1 mb-3">
          <input
            readOnly
            value={busy ? "Generating demo link…" : (url || "Click Generate")}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 px-3 py-2 text-xs font-mono bg-slate-50 border border-slate-200 rounded text-slate-700 truncate"
            data-testid="demo-link-url"
          />
          <button
            onClick={copy}
            disabled={!url || busy}
            className="px-3 bg-[#E53935] hover:bg-[#c62828] text-white rounded text-xs flex items-center gap-1 disabled:opacity-40"
            data-testid="demo-link-copy"
            title="Copy demo link"
          >
            <Copy size={12}/>
          </button>
        </div>
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={generate}
            disabled={busy}
            className="text-xs text-slate-500 hover:text-[#E53935] inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="demo-link-regenerate"
            title="Mint a fresh token — any previously-sent URL stops working"
          >
            {busy ? <Loader2 size={11} className="animate-spin"/> : <RefreshCcw size={11}/>} Regenerate
          </button>
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E53935] hover:underline inline-flex items-center gap-1" data-testid="demo-link-open">
              Open as viewer <ExternalLink size={11}/>
            </a>
          )}
        </div>
        <div className="text-[10px] text-slate-400 mt-3 leading-relaxed space-y-1">
          <p>
            All visitors share a single <strong>Demo Viewer</strong> learner account — quiz attempts and progress on demo links accumulate together. Real learner data is unaffected.
          </p>
          <p>
            The course must be <strong>Published</strong> for the demo link to work.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
